import { type FormEvent, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { ConfirmModal, Loader, Modal } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { formatDuration } from '@/lib/format-duration';
import { userOriginBack } from '@/lib/user-detail-navigation';

interface StudentLearningSessionRow {
  id: string;
  startedAt: string;
  lastPulseAt: string;
  endedAt: string | null;
  activeSeconds: number;
  courseName: string | null;
  endReason: string | null;
  status: 'active' | 'closed' | 'limited';
  ipAddress: string | null;
}

interface StudentLearningSessionsDetail {
  user: {
    id: string;
    fullName: string;
    name: string;
    email: string;
    avatarUrl?: string | null;
  };
  summary: {
    totalActiveSeconds: number;
    todayActiveSeconds: number;
    sessionCount: number;
  };
  data: StudentLearningSessionRow[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    lastPage: number;
  };
}

interface EditSessionForm {
  minutes: string;
  seconds: string;
  startedAt: string;
  endedAt: string;
  endReason: string;
}

const END_REASON_OPTIONS = [
  { value: '', label: 'Sin motivo' },
  { value: 'afk', label: 'Inactividad (AFK)' },
  { value: 'logout', label: 'Cierre de sesión' },
  { value: 'unload', label: 'Cierre de pestaña' },
  { value: 'session_limit', label: 'Límite de sesión' },
] as const;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toDateTimeLocalValue(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDateTimeLocalValue(value: string): string | null {
  if (!value.trim()) return null;
  return new Date(value).toISOString();
}

function formFromSession(session: StudentLearningSessionRow): EditSessionForm {
  const minutes = Math.floor(session.activeSeconds / 60);
  const seconds = session.activeSeconds % 60;
  return {
    minutes: String(minutes),
    seconds: String(seconds),
    startedAt: toDateTimeLocalValue(session.startedAt),
    endedAt: toDateTimeLocalValue(session.endedAt),
    endReason: session.endReason ?? '',
  };
}

function statusLabel(status: StudentLearningSessionRow['status']): string {
  switch (status) {
    case 'active':
      return 'En curso';
    case 'limited':
      return 'Límite alcanzado';
    default:
      return 'Finalizada';
  }
}

function statusClass(status: StudentLearningSessionRow['status']): string {
  switch (status) {
    case 'active':
      return 'lms-learning-session-time__status--active';
    case 'limited':
      return 'lms-learning-session-time__status--limited';
    default:
      return 'lms-learning-session-time__status--closed';
  }
}

function endReasonLabel(reason: string | null): string {
  switch (reason) {
    case 'session_limit':
      return 'Límite de sesión';
    case 'unload':
      return 'Cierre de pestaña';
    case 'logout':
      return 'Cierre de sesión';
    case 'afk':
      return 'Inactividad (AFK)';
    default:
      return '—';
  }
}

export function LearningSessionStudentDetailPage() {
  const { userId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.PROGRESO_ADMINISTRAR);
  const [page, setPage] = useState(1);
  const [editingSession, setEditingSession] = useState<StudentLearningSessionRow | null>(null);
  const [editForm, setEditForm] = useState<EditSessionForm | null>(null);
  const [deletingSession, setDeletingSession] = useState<StudentLearningSessionRow | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const queryKey = ['learning-session-student-detail', userId, page] as const;

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      api<StudentLearningSessionsDetail>(
        `/admin/learning-sessions/student-time/${userId}?page=${page}`,
      ),
    enabled: Boolean(userId),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: {
      sessionId: string;
      body: {
        activeSeconds: number;
        startedAt: string;
        endedAt: string | null;
        endReason: string | null;
      };
    }) =>
      api<StudentLearningSessionRow>(`/admin/learning-sessions/sessions/${payload.sessionId}`, {
        method: 'PATCH',
        body: payload.body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learning-session-student-detail', userId] });
      void queryClient.invalidateQueries({ queryKey: ['learning-session-student-time'] });
      setEditingSession(null);
      setEditForm(null);
      setFormError(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (sessionId: string) =>
      api<{ deleted: true }>(`/admin/learning-sessions/sessions/${sessionId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learning-session-student-detail', userId] });
      void queryClient.invalidateQueries({ queryKey: ['learning-session-student-time'] });
      setDeletingSession(null);
    },
  });

  const sessions = data?.data ?? [];
  const meta = data?.meta;
  const perPage = meta?.perPage ?? 20;
  const total = meta?.total ?? sessions.length;
  const lastPage = meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);
  const name = data?.user.fullName || data?.user.name || 'Estudiante';

  function openEdit(session: StudentLearningSessionRow) {
    if (!canManage) return;
    setEditingSession(session);
    setEditForm(formFromSession(session));
    setFormError(null);
  }

  function closeEdit() {
    if (updateMutation.isPending) return;
    setEditingSession(null);
    setEditForm(null);
    setFormError(null);
  }

  function handleEditSubmit(event: FormEvent) {
    event.preventDefault();
    if (!editingSession || !editForm) return;

    const minutes = Number.parseInt(editForm.minutes, 10) || 0;
    const seconds = Number.parseInt(editForm.seconds, 10) || 0;
    if (minutes < 0 || seconds < 0 || seconds > 59) {
      setFormError('Revisa el tiempo activo ingresado.');
      return;
    }

    const activeSeconds = minutes * 60 + seconds;
    if (activeSeconds > 86400) {
      setFormError('El tiempo activo no puede superar 24 horas.');
      return;
    }

    const startedAt = fromDateTimeLocalValue(editForm.startedAt);
    if (!startedAt) {
      setFormError('La fecha de inicio es obligatoria.');
      return;
    }

    updateMutation.mutate({
      sessionId: editingSession.id,
      body: {
        activeSeconds,
        startedAt,
        endedAt: fromDateTimeLocalValue(editForm.endedAt),
        endReason: editForm.endReason || null,
      },
    });
  }

  const studentName = data?.user?.fullName ?? data?.user?.name;
  const back = userOriginBack(
    searchParams,
    '/progreso/tiempo-conexion',
    'Volver a Tiempo de conexión por alumno',
    userId,
  );

  return (
    <div className="lms-learning-session-time lms-site-admin-hub-page font-sans">
      <header className="flex items-start gap-4">
        <HubBackLink to={back.to} label={back.label} />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Sesiones de conexión</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {studentName ? `${studentName} · Detalle de tiempo activo por sesión` : 'Detalle de tiempo activo por sesión'}
          </p>
        </div>
      </header>

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : data ? (
        <>
          <section className="lms-learning-session-time__summary-card">
            <div className="lms-learning-session-time__student">
              {data.user.avatarUrl ? (
                <img src={data.user.avatarUrl} alt="" className="lms-learning-session-time__avatar-img" />
              ) : (
                <span className="lms-learning-session-time__avatar" aria-hidden="true">
                  {initials(name)}
                </span>
              )}
              <div className="lms-learning-session-time__student-meta">
                <span className="lms-learning-session-time__student-name">{name}</span>
                <span className="lms-learning-session-time__student-email">{data.user.email}</span>
              </div>
            </div>
            <div className="lms-learning-session-time__summary-grid">
              <div>
                <span className="lms-learning-session-time__summary-label">Tiempo total</span>
                <strong>{formatDuration(data.summary.totalActiveSeconds)}</strong>
              </div>
              <div>
                <span className="lms-learning-session-time__summary-label">Hoy</span>
                <strong>{formatDuration(data.summary.todayActiveSeconds)}</strong>
              </div>
              <div>
                <span className="lms-learning-session-time__summary-label">Sesiones</span>
                <strong>{data.summary.sessionCount}</strong>
              </div>
            </div>
          </section>

          <section className="lms-learning-session-time__table-card">
            <div className="lms-learning-session-time__table-scroll">
              <table className="lms-learning-session-time__table">
                <thead>
                  <tr>
                    <th>Inicio</th>
                    <th>IP</th>
                    <th>Última actividad</th>
                    <th>Fin</th>
                    <th>Tiempo activo</th>
                    <th>Curso</th>
                    <th>Estado</th>
                    <th>Motivo de cierre</th>
                    {canManage ? <th className="lms-learning-session-time__th--actions">Acciones</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {sessions.length === 0 ? (
                    <tr>
                      <td colSpan={canManage ? 9 : 8} className="lms-learning-session-time__empty">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          timer_off
                        </span>
                        <p>Este estudiante aún no tiene sesiones registradas.</p>
                      </td>
                    </tr>
                  ) : (
                    sessions.map((session) => (
                      <tr key={session.id}>
                        <td>{formatDateTime(session.startedAt)}</td>
                        <td>{session.ipAddress ?? '—'}</td>
                        <td>{formatDateTime(session.lastPulseAt)}</td>
                        <td>{formatDateTime(session.endedAt)}</td>
                        <td>
                          <span className="lms-learning-session-time__duration">
                            {formatDuration(session.activeSeconds)}
                          </span>
                        </td>
                        <td>{session.courseName ?? '—'}</td>
                        <td>
                          <span className={`lms-learning-session-time__status ${statusClass(session.status)}`}>
                            {statusLabel(session.status)}
                          </span>
                        </td>
                        <td>{endReasonLabel(session.endReason)}</td>
                        {canManage ? (
                          <td className="lms-learning-session-time__actions-cell">
                            <button
                              type="button"
                              className="lms-learning-session-time__action"
                              aria-label="Editar sesión"
                              title="Editar"
                              onClick={() => openEdit(session)}
                            >
                              <span className="material-symbols-outlined" aria-hidden="true">
                                edit
                              </span>
                            </button>
                            <button
                              type="button"
                              className="lms-learning-session-time__action lms-learning-session-time__action--danger"
                              aria-label="Eliminar sesión"
                              title="Eliminar"
                              onClick={() => setDeletingSession(session)}
                            >
                              <span className="material-symbols-outlined" aria-hidden="true">
                                delete
                              </span>
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 ? (
              <div className="lms-contracts-generated-footer">
                <span className="lms-contracts-generated-footer__meta">
                  Mostrando {rangeStart} a {rangeEnd} de {total} resultados
                </span>
                <div className="lms-contracts-generated-pagination">
                  <button
                    type="button"
                    className="lms-contracts-generated-pagination__btn"
                    aria-label="Página anterior"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <button
                    type="button"
                    className="lms-contracts-generated-pagination__btn"
                    aria-label="Página siguiente"
                    disabled={page >= lastPage}
                    onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  >
                    <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {canManage ? (
      <Modal
        open={Boolean(editingSession && editForm)}
        onClose={closeEdit}
        title="Editar sesión"
        size="md"
        footer={
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-dash-outline-variant px-6 py-4">
            <button
              type="button"
              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-dash-outline-variant bg-secondary-200 px-4 py-2.5 text-sm font-medium text-dash-on-surface-variant transition-colors hover:bg-secondary-300 disabled:opacity-50"
              onClick={closeEdit}
              disabled={updateMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="edit-learning-session-form"
              className="inline-flex shrink-0 items-center justify-center rounded-lg bg-dash-primary px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        }
      >
        {editForm ? (
          <form
            id="edit-learning-session-form"
            className="lms-learning-session-time__edit-form"
            onSubmit={handleEditSubmit}
          >
            <div className="lms-learning-session-time__edit-grid">
              <label className="lms-learning-session-time__field">
                <span>Tiempo activo (min)</span>
                <input
                  type="number"
                  className="lms-learning-session-time__input"
                  min={0}
                  value={editForm.minutes}
                  onChange={(e) => setEditForm({ ...editForm, minutes: e.target.value })}
                />
              </label>
              <label className="lms-learning-session-time__field">
                <span>Segundos</span>
                <input
                  type="number"
                  className="lms-learning-session-time__input"
                  min={0}
                  max={59}
                  value={editForm.seconds}
                  onChange={(e) => setEditForm({ ...editForm, seconds: e.target.value })}
                />
              </label>
            </div>

            <label className="lms-learning-session-time__field">
              <span>Inicio</span>
              <input
                type="datetime-local"
                className="lms-learning-session-time__input"
                value={editForm.startedAt}
                onChange={(e) => setEditForm({ ...editForm, startedAt: e.target.value })}
                required
              />
            </label>

            <label className="lms-learning-session-time__field">
              <span>Fin</span>
              <input
                type="datetime-local"
                className="lms-learning-session-time__input"
                value={editForm.endedAt}
                onChange={(e) => setEditForm({ ...editForm, endedAt: e.target.value })}
              />
            </label>

            <label className="lms-learning-session-time__field">
              <span>Motivo de cierre</span>
              <select
                className="lms-learning-session-time__input lms-learning-session-time__input--select"
                value={editForm.endReason}
                onChange={(e) => setEditForm({ ...editForm, endReason: e.target.value })}
              >
                {END_REASON_OPTIONS.map((option) => (
                  <option key={option.value || 'none'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {formError ? (
              <p className="text-sm text-red-600" role="alert">
                {formError}
              </p>
            ) : null}
          </form>
        ) : null}
      </Modal>
      ) : null}

      {canManage ? (
      <ConfirmModal
        open={Boolean(deletingSession)}
        title="Eliminar sesión"
        description={
          deletingSession
            ? `¿Eliminar la sesión iniciada el ${formatDateTime(deletingSession.startedAt)}? Esta acción no se puede deshacer.`
            : undefined
        }
        confirmLabel="Eliminar"
        variant="danger"
        loading={deleteMutation.isPending}
        onClose={() => {
          if (!deleteMutation.isPending) setDeletingSession(null);
        }}
        onConfirm={() => {
          if (deletingSession) deleteMutation.mutate(deletingSession.id);
        }}
      />
      ) : null}
    </div>
  );
}
